import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail, BRAND } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, ShieldCheck } from "lucide-react";

export default function ResetBranchPin() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    setError("");
    if (!pin || pin.length < 4) { setError("PIN must be at least 4 characters."); return; }
    if (pin !== confirm) { setError("PINs don't match."); return; }
    if (!token) { setError("Reset link is invalid."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-branch-pin", { token, new_pin: pin });
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 2000);
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || "Could not reset PIN.");
    } finally { setLoading(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-secondary p-6">
      <Card className="w-full max-w-md p-6">
        <div className="flex items-center gap-3">
          <img src={BRAND.icon} alt="" className="h-9 w-9" />
          <div className="leading-tight">
            <div className="font-heading text-lg font-semibold text-brand">{BRAND.name}</div>
            <div className="text-[10px] text-ink-muted">{BRAND.tagline}</div>
          </div>
        </div>
        <h1 className="mt-6 font-heading text-2xl font-semibold">Reset your Branch PIN</h1>
        <p className="mt-1 text-sm text-ink-muted">Choose a new PIN — you'll use it at the branch-selection screen after every login.</p>

        {done ? (
          <div className="mt-6 rounded-md border border-leaf/30 bg-leaf-50 p-4 text-sm text-leaf-700" data-testid="reset-pin-success">
            <div className="flex items-center gap-2 font-semibold"><ShieldCheck size={16} />PIN updated.</div>
            <div className="mt-1 text-xs">Taking you to the login page…</div>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-3">
            <div>
              <Label htmlFor="pin">New Branch PIN</Label>
              <div className="relative mt-1.5">
                <KeyRound size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-subtle" />
                <Input id="pin" type="password" inputMode="numeric" value={pin} onChange={(e) => setPin(e.target.value)} placeholder="4–8 characters" className="pl-9 tracking-widest" data-testid="reset-pin-new" />
              </div>
            </div>
            <div>
              <Label htmlFor="confirm">Confirm new PIN</Label>
              <Input id="confirm" type="password" inputMode="numeric" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1.5 tracking-widest" data-testid="reset-pin-confirm" />
            </div>
            {error && <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive" data-testid="reset-pin-error">{error}</div>}
            <Button type="submit" disabled={loading} className="w-full bg-brand hover:bg-brand-700" data-testid="reset-pin-submit">{loading ? "Updating…" : "Set new PIN"}</Button>
          </form>
        )}
      </Card>
    </div>
  );
}
